import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { z } from "zod";

export const SelectorProductLineSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    parent: z
        .object({
            "@id": z.string(),
            id: z.number(),
            name: z.string(),
        })
        .optional()
        .nullable(),
});

export type SelectorProductLine = z.infer<typeof SelectorProductLineSchema>;

interface UseProductLineSelectorOptions {
    searchText?: string;
    enabled?: boolean;
    itemsPerPage?: number;
    excludeId?: number;
}

export const useProductLineSelector = (options: UseProductLineSelectorOptions = {}) => {
    const { searchText = "", enabled = true, itemsPerPage = 10, excludeId } = options;

    const fetchProductLines = async () => {
        const response = await loggedInApiCall.get("/api/product_lines", {
            params: {
                name: searchText,
                itemsPerPage: itemsPerPage,
                "order[name]": "asc",
            },
        });

        const productLines = zodParseWithLog(
            response.data["hydra:member"],
            z.array(SelectorProductLineSchema),
            "productLineSelector",
        );

        // Filter out the excluded product line and its descendants to prevent circular references
        if (excludeId) {
            return productLines.filter((productLine) => {
                // Don't include the line being edited
                if (productLine.id === excludeId) {
                    return false;
                }
                // Don't include lines that have the current line as parent (direct children)
                if (productLine.parent?.id === excludeId) {
                    return false;
                }
                return true;
            });
        }

        return productLines;
    };

    const query = useQuery({
        queryKey: ["productLineSelector", searchText, itemsPerPage, excludeId],
        queryFn: fetchProductLines,
        enabled: enabled,
    });

    return {
        productLines: query.data ?? [],
        ...query,
    };
};
